/**
 * Progressive Content Loader Component
 * Handles progressive loading of rich content with skeleton states
 */

import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface ProgressiveContentLoaderProps {
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  retryAction?: () => void;
  skeletonHeight?: string;
  className?: string;
}

const ProgressiveContentLoader: React.FC<ProgressiveContentLoaderProps> = ({
  children,
  loading = false,
  error = null,
  retryAction,
  skeletonHeight = 'h-32',
  className = ''
}) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!loading && !error) {
      // Add a small delay to create smooth transition
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [loading, error]);

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className={`bg-gray-200 rounded-lg ${skeletonHeight} flex items-center justify-center`}>
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading content...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="text-center">
          <div className="text-red-600 mb-2">
            <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-sm text-red-800 mb-3">{error}</p>
          {retryAction && (
            <button
              onClick={retryAction}
              className="text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'} ${className}`}
    >
      {children}
    </div>
  );
};

export default ProgressiveContentLoader;